import React, { useState, useEffect, useRef, useContext } from "react";
import { Box, Button, Select } from "@chakra-ui/react";
import { Tooltip } from "@mui/material";
import { RangeDatepicker } from "chakra-dayzed-datepicker";
import AutoComplete from "react-select";
import InputField from "components/fields/InputField";
import MerchantListModal from "../../shared/MerchantListModal";
import SubaccountListModal from "../../shared/SubaccountListModal";
import AgentListModal from "../../shared/AgentListModal";
import ProviderListModal from "../../shared/ProviderListModal";
import moment from "moment";
import { SubaccountContext } from "../../../../store/SubaccountProvider";

interface ProviderAccount {
  label: string;
  value: string;
}

const InputStyle =
  "daterange-picker font-medium tracking-tight text-navy-700 h-10 w-full items-center shadow-sm rounded-md border bg-white/0 p-3 text-sm outline-none shadow-sm border-gray-300 dark:!border-white/10 dark:text-white";

const dateRangePropsConfigs = {
  dateNavBtnProps: {
    variant: "outline",
  },
  dayOfMonthBtnProps: {
    defaultBtnProps: {
      borderColor: "rgb(233 236 239 / 1)", // Default border color
      borderRadius: "2px",
      _hover: {
        background: "gray", // Background color on hover
        color: "white",
      },
    },
    isInRangeBtnProps: {
      background: "gray", // Background color for dates in the selected range
      color: "white", // Text color for dates in the selected range
    },
    selectedBtnProps: {
      background: "black", // Background color for selected date
      color: "white", // Text color for selected date
    },
    todayBtnProps: {
      // color: "#422afb", // Background color for today's date
      // fontWeight: "bold",
    },
  },
  inputProps: {
    // minWidth: "230px",
    className: InputStyle,
  },
  popoverCompProps: {
    popoverContentProps: {
      backgroundColor: "white",
      borderRadius: "8px",
      boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.1)", // Apply custom box shadow
    },
  },
  calendarPanelProps: {
    wrapperProps: {
      borderColor: "#81E6D9", // Border color for the calendar wrapper
    },
    contentProps: {
      borderWidth: 0,
    },
    headerProps: {
      padding: "5px",
    },
    dividerProps: {
      display: "none",
    },
  },
  weekdayLabelProps: {
    fontWeight: "bold",
    fontSize: 16,
    color: "rgb(26 36 74)",
  },
  dateHeadingProps: {
    fontWeight: "bold",
    fontSize: 16,
    color: "rgb(26 36 74)",
  },
};

const TransactionSearch: React.FC<{
  showFilters: boolean;
  userPermissions: any;
  isFilterLoading: boolean;
  filters: any;
  setFilters: any;
  selectedDates: any;
  setSelectedDates: any;
  handleSearchSubmit(): void;
  handleSearchClear(): void;
  assetList: any;
  dataFor: string;
  setDataFor: any;
  setIsReadyToExport: any;
  setDateFiltersForExport: any;
}> = ({
  userPermissions,
  showFilters,
  isFilterLoading,
  filters,
  setFilters,
  selectedDates,
  setSelectedDates,
  handleSearchSubmit,
  handleSearchClear,
  assetList,
  dataFor,
  setDataFor,
  setIsReadyToExport,
  setDateFiltersForExport,
}) => {
  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const { subaccountBalance, isGettingBalance } = useContext(SubaccountContext);

  // Modal states for merchant, subaccount, agent, and provider selection
  const [isMerchantModalOpen, setIsMerchantModalOpen] = useState<boolean>(false);
  const [isSubaccountModalOpen, setIsSubaccountModalOpen] = useState<boolean>(false);
  const [isAgentModalOpen, setIsAgentModalOpen] = useState<boolean>(false);
  const [isProviderModalOpen, setIsProviderModalOpen] = useState<boolean>(false);
  const [selectedMerchant, setSelectedMerchant] = useState<any>(null);
  const [selectedSubaccount, setSelectedSubaccount] = useState<any>(null);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [selectedProvider, setSelectedProvider] = useState<any>(null);

  const [
    providerAccountAutoCompleteOptions,
    setProviderAccountAutoCompleteOptions,
  ] = useState<any>(null);

  const providerAccountAutoCompleteRef = useRef(null);

  // Build asset list from subaccountBalance for agents
  const agentAssetList = React.useMemo(() => {
    if (userRole !== "agent" || !subaccountBalance || subaccountBalance.length === 0) {
      return null;
    }

    // Extract unique assets from subaccountBalance
    const uniqueAssets = subaccountBalance.map((item: any) => ({
      name: item.asset,
      decimal: item.decimal ?? 2,
      crypto: item.crypto
    }));

    return uniqueAssets;
  }, [userRole, subaccountBalance]);

  const handleDateChange = (dates: Date[]) => {
    setIsReadyToExport(false);
    setSelectedDates(dates);

    const data = {
      from: moment(dates[0], "DD/MM/YYYY").format("MMM D, YYYY 00:00"),
      to: moment(dates[1], "DD/MM/YYYY").format("MMM D, YYYY 23:59"),
    };
    setDateFiltersForExport(data);
  };

  const handleInputChange = async (event: any) => {
    setIsReadyToExport(false);
    const { id, value } = event.target;
    setFilters({ ...filters, [id]: value });
  };

  const handleDataForChange = (value: string) => {
    setIsReadyToExport(false);
    setDataFor(value);
  };

  // Handler for merchant selection from modal
  const handleMerchantSelect = (merchantData: any) => {
    setIsReadyToExport(false);
    setSelectedMerchant(merchantData);
    setSelectedSubaccount(null);
    setSelectedAgent(null);
    setSelectedProvider(null);
    localStorage.setItem("merchantIdToFilterTransaction", merchantData.id);
    localStorage.setItem("merchantNameToFilterTransaction", merchantData.name);
    localStorage.setItem("subaccountIdToFilterTransaction", "");
    localStorage.setItem("subaccountNameToFilterTransaction", "");
    localStorage.setItem("agentIdToFilterTransaction", "");
    localStorage.setItem("agentNameToFilterTransaction", "");
    localStorage.setItem("providerIdToFilterTransaction", "");
    localStorage.setItem("providerNameToFilterTransaction", "");
    localStorage.setItem("providerAccountIdToFilterTransaction", "");
    localStorage.setItem("providerAccountNameToFilterTransaction", "");
    providerAccountAutoCompleteRef.current?.clearValue();
    setProviderAccountAutoCompleteOptions(null);
    setDataFor("");
    setFilters({
      ...filters,
      merchant_id: merchantData.id,
      subaccount_id: "",
      agent_id: "",
      provider_id: "",
      provider_account_id: "",
    });
  };

  // Clear merchant selection
  const handleClearMerchant = () => {
    setIsReadyToExport(false);
    setSelectedMerchant(null);
    setSelectedSubaccount(null);
    setSelectedAgent(null);
    setSelectedProvider(null);
    localStorage.setItem("merchantIdToFilterTransaction", "");
    localStorage.setItem("merchantNameToFilterTransaction", "");
    localStorage.setItem("subaccountIdToFilterTransaction", "");
    localStorage.setItem("subaccountNameToFilterTransaction", "");
    localStorage.setItem("agentIdToFilterTransaction", "");
    localStorage.setItem("agentNameToFilterTransaction", "");
    localStorage.setItem("providerIdToFilterTransaction", "");
    localStorage.setItem("providerNameToFilterTransaction", "");
    localStorage.setItem("providerAccountIdToFilterTransaction", "");
    localStorage.setItem("providerAccountNameToFilterTransaction", "");
    providerAccountAutoCompleteRef.current?.clearValue();
    setProviderAccountAutoCompleteOptions(null);
    setDataFor("");
    setFilters({
      ...filters,
      merchant_id: "",
      subaccount_id: "",
      agent_id: "",
      provider_id: "",
      provider_account_id: "",
    });
  };

  // Handler for subaccount selection from modal
  const handleSubaccountSelect = (subaccountData: any) => {
    setIsReadyToExport(false);
    setSelectedSubaccount(subaccountData);
    setSelectedAgent(null);
    setSelectedProvider(null);
    localStorage.setItem("subaccountIdToFilterTransaction", subaccountData.id);
    localStorage.setItem("subaccountNameToFilterTransaction", subaccountData.suba_name);
    localStorage.setItem("agentIdToFilterTransaction", "");
    localStorage.setItem("agentNameToFilterTransaction", "");
    localStorage.setItem("providerIdToFilterTransaction", "");
    localStorage.setItem("providerNameToFilterTransaction", "");
    localStorage.setItem("providerAccountIdToFilterTransaction", "");
    localStorage.setItem("providerAccountNameToFilterTransaction", "");
    providerAccountAutoCompleteRef.current?.clearValue();
    setProviderAccountAutoCompleteOptions(null);
    setDataFor("");
    setFilters({
      ...filters,
      subaccount_id: subaccountData.id,
      agent_id: "",
      provider_id: "",
      provider_account_id: "",
    });
  };

  // Clear subaccount selection
  const handleClearSubaccount = () => {
    setIsReadyToExport(false);
    setSelectedSubaccount(null);
    setSelectedAgent(null);
    setSelectedProvider(null);
    localStorage.setItem("subaccountIdToFilterTransaction", "");
    localStorage.setItem("subaccountNameToFilterTransaction", "");
    localStorage.setItem("agentIdToFilterTransaction", "");
    localStorage.setItem("agentNameToFilterTransaction", "");
    localStorage.setItem("providerIdToFilterTransaction", "");
    localStorage.setItem("providerNameToFilterTransaction", "");
    localStorage.setItem("providerAccountIdToFilterTransaction", "");
    localStorage.setItem("providerAccountNameToFilterTransaction", "");
    providerAccountAutoCompleteRef.current?.clearValue();
    setProviderAccountAutoCompleteOptions(null);
    setDataFor("");
    setFilters({
      ...filters,
      subaccount_id: "",
      agent_id: "",
      provider_id: "",
      provider_account_id: "",
    });
  };

  // Handler for agent selection from modal
  const handleAgentSelect = (agentData: any) => {
    setIsReadyToExport(false);
    setSelectedAgent(agentData);
    localStorage.setItem("agentIdToFilterTransaction", agentData.id);
    localStorage.setItem("agentNameToFilterTransaction", agentData.suba_name);
    setFilters({ ...filters, agent_id: agentData.id });
  };

  // Clear agent selection
  const handleClearAgent = () => {
    setIsReadyToExport(false);
    setSelectedAgent(null);
    localStorage.setItem("agentIdToFilterTransaction", "");
    localStorage.setItem("agentNameToFilterTransaction", "");
    setDataFor("");
    setFilters({ ...filters, agent_id: "" });
  };

  // Handler for provider selection from modal
  const handleProviderSelect = (providerData: any) => {
    setIsReadyToExport(false);
    setSelectedProvider(providerData);
    localStorage.setItem("providerIdToFilterTransaction", providerData.id);
    localStorage.setItem("providerNameToFilterTransaction", providerData.name);
    localStorage.setItem("providerAccountIdToFilterTransaction", "");
    localStorage.setItem("providerAccountNameToFilterTransaction", "");
    setProviderAccountAutoCompleteOptions(null);
    providerAccountAutoCompleteRef.current?.clearValue();
    buildUpProviderAccount(providerData.id, providerData);
    setFilters({ ...filters, provider_id: providerData.id, provider_account_id: "" });
  };

  // Clear provider selection
  const handleClearProvider = () => {
    setIsReadyToExport(false);
    setSelectedProvider(null);
    localStorage.setItem("providerIdToFilterTransaction", "");
    localStorage.setItem("providerNameToFilterTransaction", "");
    localStorage.setItem("providerAccountIdToFilterTransaction", "");
    localStorage.setItem("providerAccountNameToFilterTransaction", "");
    setProviderAccountAutoCompleteOptions(null);
    providerAccountAutoCompleteRef.current?.clearValue();
    setDataFor("");
    setFilters({ ...filters, provider_id: "", provider_account_id: "" });
  };

  const handleProviderAccountAutoCompleteChange = (item: any) => {
    setIsReadyToExport(false);
    localStorage.setItem("providerAccountIdToFilterTransaction", "");
    localStorage.setItem("providerAccountNameToFilterTransaction", "");
    if (item !== null) {
      localStorage.setItem("providerAccountIdToFilterTransaction", item.value);
      localStorage.setItem("providerAccountNameToFilterTransaction", item.label);
      setFilters({ ...filters, provider_account_id: item.value });
    } else {
      setFilters({ ...filters, provider_account_id: "" });
      setProviderAccountAutoCompleteOptions(null);
      if (selectedProvider) {
        buildUpProviderAccount(selectedProvider.id, selectedProvider);
      }
    }
  };

  const noProviderAccountOptionsMessageRenderer = ({
    inputValue,
  }: {
    inputValue: string;
  }) => {
    return (
      <div className="text-sm">
        Provider account not found for "{inputValue}"
      </div>
    );
  };

  const buildUpProviderAccount = (
    providerIdToFilterProviderAccount: string,
    providerData?: any
  ) => {
    const providerAccountList: ProviderAccount[] = [];
    if (providerData && providerIdToFilterProviderAccount !== "") {
      if (
        providerData.provider_account &&
        Array.isArray(providerData.provider_account) &&
        providerData.provider_account.length > 0
      ) {
        providerData.provider_account.forEach((account: any) => {
          if (account?.id && account?.account_name) {
            providerAccountList.push({
              label: `${providerData.name} (${account.account_name})`,
              value: account.id,
            });
          }
        });
      }
      setProviderAccountAutoCompleteOptions(providerAccountList);
    }
  };

  const handleClear = () => {
    if (userRole === "superadmin") {
      setSelectedMerchant(null);
      setSelectedSubaccount(null);
      setSelectedAgent(null);
      setSelectedProvider(null);
      localStorage.setItem("merchantIdToFilterTransaction", "");
      localStorage.setItem("merchantNameToFilterTransaction", "");
      localStorage.setItem("subaccountIdToFilterTransaction", "");
      localStorage.setItem("subaccountNameToFilterTransaction", "");
      localStorage.setItem("agentIdToFilterTransaction", "");
      localStorage.setItem("agentNameToFilterTransaction", "");
      localStorage.setItem("providerIdToFilterTransaction", "");
      localStorage.setItem("providerNameToFilterTransaction", "");
      localStorage.setItem("providerAccountIdToFilterTransaction", "");
      localStorage.setItem("providerAccountNameToFilterTransaction", "");
      providerAccountAutoCompleteRef.current?.clearValue();
      setProviderAccountAutoCompleteOptions(null);
      setDataFor("all");
    }
    if (userRole === "merchant") {
      setSelectedSubaccount(null);
      localStorage.setItem("subaccountIdToFilterTransaction", "");
      localStorage.setItem("subaccountNameToFilterTransaction", "");
    }
    handleSearchClear();
  };

  // If not showing filters, render nothing (button lives outside in the toolbar)
  if (!showFilters) return null;

  return (
    <>
      <Box className="mb-4 mt-2 rounded-lg bg-white p-6 shadow-lg ring-1 ring-gray-100">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
          <div className={`search-picker-custom tracking-tight text-gray-900 ${userRole === "merchant" ? "" : "md:col-span-2"}`}>
            <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
              Date
            </label>
            <RangeDatepicker
              disabled={isFilterLoading}
              selectedDates={selectedDates}
              onDateChange={(dates) => handleDateChange(dates)}
              propsConfigs={dateRangePropsConfigs}
              configs={{
                dateFormat: "MMM d, yyyy",
              }}
              maxDate={new Date()}
            />
          </div>
          {userRole === "superadmin" && (
            <>
              <div className="tracking-tight text-gray-900">
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                  Merchant
                </label>
                <Tooltip
                  title={selectedMerchant ? selectedMerchant.name : "All"}
                  arrow
                  placement="top"
                >
                  <div
                    onClick={() => !isFilterLoading && setIsMerchantModalOpen(true)}
                    className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                      isFilterLoading
                        ? "cursor-not-allowed bg-disabledBg font-medium text-gray-700 py-[11px]"
                        : "cursor-pointer border border-gray-300 bg-white font-medium text-navy-700 shadow-sm hover:bg-gray-50 py-2.5"
                    }`}
                  >
                    <span className="truncate">
                      {selectedMerchant ? selectedMerchant.name : "All"}
                    </span>
                    {selectedMerchant && !isFilterLoading && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleClearMerchant(); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                      >
                        &times;
                      </button>
                    )}
                  </div>
                </Tooltip>
              </div>
              <div className="tracking-tight text-gray-900">
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                  Subaccount
                </label>
                <Tooltip
                  title={selectedSubaccount ? selectedSubaccount.suba_name : "All"}
                  arrow
                  placement="top"
                >
                  <div
                    onClick={() => !isFilterLoading && selectedMerchant && setIsSubaccountModalOpen(true)}
                    className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                      isFilterLoading || !selectedMerchant
                        ? "cursor-not-allowed bg-disabledBg font-medium text-gray-700 py-[11px]"
                        : "cursor-pointer border border-gray-300 bg-white font-medium text-navy-700 shadow-sm hover:bg-gray-50 py-2.5"
                    }`}
                  >
                    <span className="truncate">
                      {selectedSubaccount ? selectedSubaccount.suba_name : "All"}
                    </span>
                    {selectedSubaccount && !isFilterLoading && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleClearSubaccount(); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                      >
                        &times;
                      </button>
                    )}
                  </div>
                </Tooltip>
              </div>
              <div className="search-picker-custom tracking-tight text-gray-900">
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                  Payout ID
                </label>
                <InputField
                  disabled={isFilterLoading}
                  variant="auth"
                  extra="shadow-sm"
                  label=""
                  placeholder=""
                  id="payout_id"
                  type="text"
                  autoComplete="off"
                  value={filters.payout_id}
                  onChange={handleInputChange}
                />
              </div>
              <div className="tracking-tight text-gray-900">
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                  Asset
                </label>
                <Select
                  isDisabled={isFilterLoading}
                  id="asset"
                  value={filters.asset}
                  onChange={handleInputChange}
                  className="custom-select  relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
                >
                  <option value="">
                    {assetList ? "All" : "Retrieving..."}
                  </option>
                  {assetList &&
                    assetList.filter((item: any) => !item.crypto).length > 0 &&
                    assetList.filter((item: any) => !item.crypto) && (
                      <optgroup label="Fiat">
                        {assetList &&
                          assetList
                            .filter((item: any) => !item.crypto)
                            .map((asset: any) => (
                              <option key={asset.name} value={asset.name}>
                                {asset.name}
                              </option>
                            ))}
                      </optgroup>
                    )}
                  {assetList &&
                    assetList.filter((item: any) => item.crypto).length > 0 &&
                    assetList.filter((item: any) => item.crypto) && (
                      <optgroup label="Crypto">
                        {assetList &&
                          assetList
                            .filter((item: any) => item.crypto)
                            .map((asset: any) => (
                              <option key={asset.name} value={asset.name}>
                                {asset.name}
                              </option>
                            ))}
                      </optgroup>
                    )}
                </Select>
              </div>
              <div className="tracking-tight text-gray-900">
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                  Status
                </label>
                <Select
                  isDisabled={isFilterLoading}
                  id="status"
                  value={filters.status}
                  onChange={handleInputChange}
                  className="custom-select relative  h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
                >
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="complete">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="failed">Failed</option>
                </Select>
              </div>
              <div className="tracking-tight text-gray-900">
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                  Agent
                </label>
                <Tooltip
                  title={selectedAgent ? selectedAgent.suba_name : "All"}
                  arrow
                  placement="top"
                >
                  <div
                    onClick={() => !isFilterLoading && setIsAgentModalOpen(true)}
                    className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                      isFilterLoading
                        ? "cursor-not-allowed bg-disabledBg font-medium text-gray-700 py-[11px]"
                        : "cursor-pointer border border-gray-300 bg-white font-medium text-navy-700 shadow-sm hover:bg-gray-50 py-2.5"
                    }`}
                  >
                    <span className="truncate">
                      {selectedAgent ? selectedAgent.suba_name : "All"}
                    </span>
                    {selectedAgent && !isFilterLoading && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleClearAgent(); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                      >
                        &times;
                      </button>
                    )}
                  </div>
                </Tooltip>
                {filters.agent_id !== "" && filters.provider_id !== "" && (
                  <div className="mt-2 flex items-start gap-2">
                    <input
                      id="display-agent-transaction-only"
                      type="checkbox"
                      className="accent-black mt-1 h-4 w-4 cursor-pointer text-navy-600"
                      onChange={(e) =>
                        handleDataForChange(
                          e.target.checked ? "reseller" : "all"
                        )
                      }
                      checked={dataFor === "reseller"}
                    />
                    <label
                      htmlFor="display-agent-transaction-only"
                      className="cursor-pointer text-sm !leading-[1.7] text-navy-700 dark:text-white"
                    >
                      Display agent transactions only
                    </label>
                  </div>
                )}
              </div>
              <div className="tracking-tight text-gray-900">
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                  Provider
                </label>
                <Tooltip
                  title={selectedProvider ? selectedProvider.name : "All"}
                  arrow
                  placement="top"
                >
                  <div
                    onClick={() => !isFilterLoading && setIsProviderModalOpen(true)}
                    className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                      isFilterLoading
                        ? "cursor-not-allowed bg-disabledBg font-medium text-gray-700 py-[11px]"
                        : "cursor-pointer border border-gray-300 bg-white font-medium text-navy-700 shadow-sm hover:bg-gray-50 py-2.5"
                    }`}
                  >
                    <span className="truncate">
                      {selectedProvider ? selectedProvider.name : "All"}
                    </span>
                    {selectedProvider && !isFilterLoading && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleClearProvider(); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                      >
                        &times;
                      </button>
                    )}
                  </div>
                </Tooltip>
                {filters.agent_id !== "" && filters.provider_id !== "" && (
                  <div className="mt-2 flex items-start gap-2">
                    <input
                      id="display-provider-transaction-only"
                      type="checkbox"
                      className="accent-black mt-1 h-4 w-4 cursor-pointer text-navy-600"
                      onChange={(e) =>
                        handleDataForChange(
                          e.target.checked ? "provider" : "all"
                        )
                      }
                      checked={dataFor === "provider"}
                    />
                    <label
                      htmlFor="display-provider-transaction-only"
                      className="cursor-pointer text-sm !leading-[1.7] text-navy-700 dark:text-white"
                    >
                      Display provider transactions only
                    </label>
                  </div>
                )}
              </div>
              <div className="tracking-tight text-gray-900">
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                  Provider Account
                </label>
                <AutoComplete
                  ref={providerAccountAutoCompleteRef}
                  className="autocomplete-override"
                  isDisabled={
                    isFilterLoading ||
                    filters.provider_id === "" ||
                    providerAccountAutoCompleteOptions === null ||
                    providerAccountAutoCompleteOptions.length === 0
                  }
                  placeholder={
                    isFilterLoading
                      ? "Retrieving..."
                      : providerAccountAutoCompleteOptions === null
                      ? "All"
                      : providerAccountAutoCompleteOptions.length === 0
                      ? "N/A"
                      : "All"
                  }
                  options={providerAccountAutoCompleteOptions}
                  classNamePrefix="custom-autocomplete"
                  onChange={(item: any) =>
                    handleProviderAccountAutoCompleteChange(item)
                  }
                  noOptionsMessage={noProviderAccountOptionsMessageRenderer}
                  isClearable
                />
              </div>
              <div
                className={`flex gap-2 md:col-span-5 ${
                  filters.agent_id !== "" && filters.provider_id !== ""
                    ? "items-center justify-end"
                    : "items-end justify-end"
                }`}
              >
                <Button
                  isDisabled={isFilterLoading}
                  onClick={() => handleClear()}
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-700"
                >
                  Clear
                </Button>

                <Button
                  isDisabled={isFilterLoading}
                  onClick={() => handleSearchSubmit()}
                  className="btn-primary relative overflow-hidden rounded-md px-6 py-2.5 text-sm font-medium text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span className="z-9 relative tracking-tight">
                    Search Transactions
                  </span>
                  <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
                </Button>
              </div>
            </>
          )}
          {userRole === "agent" && (
            <>
              <div className="tracking-tight text-gray-900  md:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                  Asset
                </label>
                <Select
                  isDisabled={isFilterLoading}
                  id="asset"
                  value={filters.asset}
                  onChange={handleInputChange}
                  className="custom-select  relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
                >
                  <option value="">
                    {isGettingBalance ? "Retrieving..." : agentAssetList ? "All" : "Retrieving..."}
                  </option>
                  {agentAssetList &&
                    agentAssetList.filter((item: any) => !item.crypto).length > 0 &&
                    agentAssetList.filter((item: any) => !item.crypto) && (
                      <optgroup label="Fiat">
                        {agentAssetList &&
                          agentAssetList
                            .filter((item: any) => !item.crypto)
                            .map((asset: any) => (
                              <option key={asset.name} value={asset.name}>
                                {asset.name}
                              </option>
                            ))}
                      </optgroup>
                    )}
                  {agentAssetList &&
                    agentAssetList.filter((item: any) => item.crypto).length > 0 &&
                    agentAssetList.filter((item: any) => item.crypto) && (
                      <optgroup label="Crypto">
                        {agentAssetList &&
                          agentAssetList
                            .filter((item: any) => item.crypto)
                            .map((asset: any) => (
                              <option key={asset.name} value={asset.name}>
                                {asset.name}
                              </option>
                            ))}
                      </optgroup>
                    )}
                </Select>
              </div>
              <div className="tracking-tight text-gray-900">
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                  Status
                </label>
                <Select
                  isDisabled={isFilterLoading}
                  id="status"
                  value={filters.status}
                  onChange={handleInputChange}
                  className="custom-select relative  h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
                >
                  <option value="">All</option>
                  <option value="pending">PENDING</option>
                  <option value="complete">COMPLETE</option>
                  <option value="cancelled">CANCELLED</option>
                  <option value="failed">FAILED</option>
                </Select>
              </div>
              <div className="flex gap-2 md:col-span-5">
                <div className="flex flex-1 items-end justify-end">
                  <Button
                    isDisabled={isFilterLoading}
                    onClick={() => handleClear()}
                    className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-700"
                  >
                    Clear
                  </Button>
                  <Button
                    isDisabled={isFilterLoading}
                    onClick={() => handleSearchSubmit()}
                    className="btn-primary relative overflow-hidden rounded-md px-6 py-2.5 text-sm font-medium text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <span className="z-9 relative tracking-tight">
                      Search Transactions
                    </span>
                    <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
                  </Button>
                </div>
              </div>
            </>
          )}
          {userRole !== "superadmin" && userRole !== "agent" && (
            <>
              {userRole === "merchant" && (
                <div className="tracking-tight text-gray-900">
                  <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                    Subaccount
                  </label>
                  <Tooltip
                    title={selectedSubaccount ? selectedSubaccount.suba_name : "All"}
                    arrow
                    placement="top"
                  >
                    <div
                      onClick={() => !isFilterLoading && setIsSubaccountModalOpen(true)}
                      className={`relative flex w-full items-center rounded-md pl-4 pr-8 text-sm ${
                        isFilterLoading
                          ? "cursor-not-allowed bg-disabledBg font-medium text-gray-700 py-[11px]"
                          : "cursor-pointer border border-gray-300 bg-white font-medium text-navy-700 shadow-sm hover:bg-gray-50 py-2.5"
                      }`}
                    >
                      <span className="truncate">
                        {selectedSubaccount ? selectedSubaccount.suba_name : "All"}
                      </span>
                      {selectedSubaccount && !isFilterLoading && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleClearSubaccount(); }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-lg font-medium text-gray-400 hover:text-gray-600"
                        >
                          &times;
                        </button>
                      )}
                    </div>
                  </Tooltip>
                </div>
              )}
              <div className="tracking-tight text-gray-900">
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                  Asset
                </label>
                <Select
                  isDisabled={isFilterLoading}
                  id="asset"
                  value={filters.asset}
                  onChange={handleInputChange}
                  className="custom-select  relative h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
                >
                  <option value="">
                    {assetList ? "All" : "Retrieving..."}
                  </option>
                  {assetList &&
                    assetList.filter((item: any) => !item.crypto).length > 0 &&
                    assetList.filter((item: any) => !item.crypto) && (
                      <optgroup label="Fiat">
                        {assetList &&
                          assetList
                            .filter((item: any) => !item.crypto)
                            .map((asset: any) => (
                              <option key={asset.name} value={asset.name}>
                                {asset.name}
                              </option>
                            ))}
                      </optgroup>
                    )}
                  {assetList &&
                    assetList.filter((item: any) => item.crypto).length > 0 &&
                    assetList.filter((item: any) => item.crypto) && (
                      <optgroup label="Crypto">
                        {assetList &&
                          assetList
                            .filter((item: any) => item.crypto)
                            .map((asset: any) => (
                              <option key={asset.name} value={asset.name}>
                                {asset.name}
                              </option>
                            ))}
                      </optgroup>
                    )}
                </Select>
              </div>
              <div className="tracking-tight text-gray-900">
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                  Status
                </label>
                <Select
                  isDisabled={isFilterLoading}
                  id="status"
                  value={filters.status}
                  onChange={handleInputChange}
                  className="custom-select relative  h-10 w-full rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm font-bold text-navy-700 shadow-sm focus:outline-none"
                >
                  <option value="">All</option>
                  <option value="pending">PENDING</option>
                  <option value="complete">COMPLETE</option>
                  <option value="cancelled">CANCELLED</option>
                  <option value="failed">FAILED</option>
                </Select>
              </div>
              <div className="search-picker-custom tracking-tight text-gray-900">
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-white">
                  Payout ID
                </label>
                <InputField
                  disabled={isFilterLoading}
                  variant="auth"
                  extra="shadow-sm"
                  label=""
                  placeholder=""
                  id="payout_id"
                  type="text"
                  autoComplete="off"
                  value={filters.payout_id}
                  onChange={handleInputChange}
                />
              </div>
              <div className="flex gap-2 md:col-span-5">
                <div className="flex flex-1 items-end justify-end">
                  <Button
                    isDisabled={isFilterLoading}
                    onClick={() => handleClear()}
                    className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-700"
                  >
                    Clear
                  </Button>
                  <Button
                    isDisabled={isFilterLoading}
                    onClick={() => handleSearchSubmit()}
                    className="btn-primary relative overflow-hidden rounded-md px-6 py-2.5 text-sm font-medium text-white shadow-md transition-colors duration-300 hover:bg-primary-500 active:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <span className="z-9 relative tracking-tight">
                      Search Transactions
                    </span>
                    <span className="absolute inset-0 z-0 scale-0 rounded-md bg-white/20 opacity-0 transition-all duration-500 group-hover:scale-150 group-hover:opacity-100" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

      {/* Merchant Selection Modal */}
      {isMerchantModalOpen && (
        <MerchantListModal
          isOpen={isMerchantModalOpen}
          onClose={() => setIsMerchantModalOpen(false)}
          onSelect={handleMerchantSelect}
        />
      )}

      {/* Subaccount Selection Modal */}
      {isSubaccountModalOpen && (selectedMerchant || userRole === "merchant") && (
        <SubaccountListModal
          isOpen={isSubaccountModalOpen}
          onClose={() => setIsSubaccountModalOpen(false)}
          onSelect={handleSubaccountSelect}
          merchantId={selectedMerchant?.id || localStorage.getItem("merchantId") || ""}
        />
      )}

      {/* Agent Selection Modal */}
      {isAgentModalOpen && (
        <AgentListModal
          isOpen={isAgentModalOpen}
          onClose={() => setIsAgentModalOpen(false)}
          onSelect={handleAgentSelect}
          subaccountId={selectedSubaccount?.id}
        />
      )}

      {/* Provider Selection Modal */}
      {isProviderModalOpen && (
        <ProviderListModal
          isOpen={isProviderModalOpen}
          onClose={() => setIsProviderModalOpen(false)}
          onSelect={handleProviderSelect}
          subaccountId={selectedSubaccount?.id}
        />
      )}
      </Box>
    </>
  );
};

export default TransactionSearch;
